// src/components/Footer.jsx
import React, { useState } from "react";
import {
  FaInstagram,
  FaFacebookF,
  FaYoutube,
  FaEnvelope,
  FaPhone,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaHeart,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";
import { Link } from "react-router-dom";

function Footer({ openTerms }) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    explore: false,
    follow: false,
    contact: false
  });

  // Toggle accordion sections on mobile
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      console.log("Subscribe:", email);
      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  const currentYear = new Date().getFullYear();
  const OWNER_NAME = "Parivar Events";

  const quickLinks = [
    { label: "Home", path: "/" },
    { label: "Venues", path: "/destination" },
    { label: "Services", path: "#services" },
    { label: "Gallery", path: "#gallery" },
    { label: "Contact", path: "#contact" },
    { label: "FAQ", path: "#faq" },
  ];

  const socialLinks = [
    { icon: FaInstagram, href: "https://instagram.com/parivarwedding", label: "Instagram", color: "#E4405F", followers: "50K+" },
    { icon: FaFacebookF, href: "https://facebook.com/parivarwedding", label: "Facebook", color: "#1877F2", followers: "25K+" },
    { icon: FaYoutube, href: "https://youtube.com/@destinationwedding_01", label: "YouTube", color: "#FF0000", followers: "12K+" },
    { icon: FaWhatsapp, href: "https://wa.me/918920514929", label: "WhatsApp", color: "#25D366", followers: "24/7" },
  ];

  // Accordion Section Component for Mobile
  const AccordionSection = ({ title, id, children }) => {
    const isExpanded = expandedSections[id];
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    
    // On desktop, always show; on mobile, toggle
    return (
      <div className="footer-section">
        <button 
          className="accordion-header"
          onClick={() => toggleSection(id)}
          aria-expanded={isExpanded}
        >
          <h6 className="footer-heading">{title}</h6>
          <span className="accordion-icon">
            {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </span>
        </button>
        <div className={`accordion-content ${isExpanded ? 'expanded' : ''}`}>
          {children}
        </div>
      </div>
    );
  };

  return (
    <footer className="site-footer">
      <style>{`
        :root {
          --primary-dark: #8B1538;
          --primary: #C41E3A;
          --primary-light: #E85D75;
          --gold: #D4AF37;
          --gold-light: #F4E4BC;
          --cream: #FFFBF5;
          --pink: #FFE4EC;
          --text-dark: #2D1F2F;
          --text-muted: #6B4E5B;
          --white: #FFFFFF;
          --shadow-soft: 0 4px 20px rgba(139, 21, 56, 0.08);
          --shadow-medium: 0 8px 30px rgba(139, 21, 56, 0.12);
        }

        .site-footer {
          background: linear-gradient(180deg, var(--cream) 0%, var(--pink) 50%, #FFF0F3 100%);
          border-top: 3px solid var(--gold);
          position: relative;
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          overflow: hidden;
        }

        /* Decorative Top Element */
        .footer-decor {
          position: absolute;
          top: -24px;
          left: 50%;
          transform: translateX(-50%);
          width: 48px;
          height: 48px;
          background: var(--gold);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
          z-index: 10;
        }

        .footer-decor::before {
          content: "✦";
          color: var(--primary-dark);
          font-size: 20px;
          line-height: 1;
        }

        /* Main Container */
        .footer-main {
          max-width: 1280px;
          margin: 0 auto;
          padding: 64px 24px 48px;
          position: relative;
          z-index: 2;
        }

        /* Grid Layout - Desktop First */
        .footer-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.5fr;
          gap: 48px;
          align-items: start;
        }

        /* Brand Column Styling */
        .footer-brand {
          max-width: 340px;
        }

        .footer-logo {
          font-family: 'Great Vibes', 'Playfair Display', cursive;
          font-size: 2.75rem;
          color: var(--primary);
          margin-bottom: 16px;
          line-height: 1.1;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .footer-tagline {
          color: var(--text-muted);
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 28px;
          font-weight: 400;
        }

        /* Contact Items */
        .contact-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .footer-contact-item {
          display: flex;
          align-items: center;
          gap: 14px;
          color: var(--text-dark);
          font-size: 0.95rem;
          transition: transform 0.2s ease;
        }

        .footer-contact-item:hover {
          transform: translateX(4px);
        }

        .footer-contact-item svg {
          color: var(--primary);
          flex-shrink: 0;
          width: 18px;
          height: 18px;
        }

        .footer-contact-item a {
          color: inherit;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
          border-bottom: 1px solid transparent;
        }

        .footer-contact-item a:hover {
          color: var(--primary);
          border-bottom-color: var(--primary-light);
        }

        /* Owner Signature */
        .owner-signature {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid rgba(196, 30, 58, 0.15);
        }

        .owner-name {
          color: var(--primary-dark);
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 4px;
        }

        .owner-title {
          font-size: 0.875rem;
          color: var(--text-muted);
          font-weight: 500;
        }

        /* Section Headers */
        .footer-heading {
          color: var(--primary-dark);
          font-size: 0.875rem;
          font-weight: 700;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-family: 'Segoe UI', system-ui, sans-serif;
        }

        /* Accordion Headers (Mobile Only) */
        .accordion-header {
          display: none;
          width: 100%;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          align-items: center;
          justify-content: space-between;
        }

        .accordion-icon {
          color: var(--primary);
          transition: transform 0.3s ease;
        }

        /* Links Styling */
        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .footer-links a {
          color: var(--text-dark);
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
        }

        .footer-links a::before {
          content: "→";
          opacity: 0;
          transform: translateX(-8px);
          transition: all 0.2s ease;
          color: var(--primary);
          font-size: 0.8rem;
        }

        .footer-links a:hover {
          color: var(--primary);
          transform: translateX(4px);
        }

        .footer-links a:hover::before {
          opacity: 1;
          transform: translateX(0);
        }

        /* Social Grid */
        .social-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .social-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: var(--white);
          border-radius: 12px;
          text-decoration: none;
          color: var(--text-dark);
          box-shadow: var(--shadow-soft);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(196, 30, 58, 0.08);
        }

        .social-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--shadow-medium);
          border-color: var(--primary-light);
        }

        .social-icon-wrap {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(196, 30, 58, 0.08);
          color: var(--primary);
          transition: all 0.3s ease;
        }

        .social-card:hover .social-icon-wrap {
          background: var(--primary);
          color: var(--white);
        }

        .social-info {
          display: flex;
          flex-direction: column;
        }

        .social-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-dark);
        }

        .social-followers {
          font-size: 0.75rem;
          color: var(--text-muted);
          font-weight: 500;
        }

        /* Newsletter Box */
        .newsletter-box {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: var(--shadow-medium);
          border: 1px solid rgba(212, 175, 55, 0.2);
        }

        .newsletter-text {
          color: var(--text-muted);
          font-size: 0.9rem;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .newsletter-form {
          display: flex;
          gap: 10px;
        }

        .newsletter-input {
          flex: 1;
          padding: 14px 18px;
          border: 2px solid rgba(196, 30, 58, 0.15);
          border-radius: 12px;
          font-size: 0.95rem;
          outline: none;
          transition: all 0.2s;
          background: var(--white);
          color: var(--text-dark);
          min-width: 0; /* Prevents flexbox overflow */
        }

        .newsletter-input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(196, 30, 58, 0.1);
        }

        .newsletter-input::placeholder {
          color: #999;
        }

        .newsletter-btn {
          padding: 14px 24px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          color: var(--white);
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
          box-shadow: 0 4px 15px rgba(196, 30, 58, 0.3);
        }

        .newsletter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(196, 30, 58, 0.4);
        }

        .newsletter-btn:active {
          transform: translateY(0);
        }

        .newsletter-success {
          color: #16A34A;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 16px;
          padding: 12px;
          background: rgba(22, 163, 74, 0.1);
          border-radius: 8px;
          font-weight: 500;
        }

        /* Bottom Bar */
        .footer-bottom {
          background: rgba(255, 255, 255, 0.6);
          border-top: 1px solid rgba(196, 30, 58, 0.1);
          padding: 24px;
          position: relative;
          z-index: 2;
        }

        .footer-bottom-content {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .copyright {
          color: var(--text-muted);
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
        }

        .copyright svg {
          color: var(--primary);
          animation: heartbeat 1.5s ease-in-out infinite;
        }

        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .legal-links {
          display: flex;
          gap: 28px;
          align-items: center;
        }

        .legal-links a,
        .legal-links button {
          color: var(--text-muted);
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          padding: 4px 0;
          position: relative;
        }

        .legal-links a::after,
        .legal-links button::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 1px;
          background: var(--primary);
          transition: width 0.3s ease;
        }

        .legal-links a:hover,
        .legal-links button:hover {
          color: var(--primary);
        }

        .legal-links a:hover::after,
        .legal-links button:hover::after {
          width: 100%;
        }

        /* Background Decoration */
        .footer-bg-decoration {
          position: absolute;
          top: 0;
          right: 0;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, transparent 70%);
          pointer-events: none;
          z-index: 1;
        }

        /* ============================
           RESPONSIVE BREAKPOINTS
           ============================ */

        /* Tablet (1024px and below) */
        @media (max-width: 1024px) {
          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }

          .footer-brand {
            max-width: 100%;
            grid-column: 1 / -1;
            text-align: center;
          }

          .contact-list {
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px;
          }

          .owner-signature {
            text-align: center;
          }

          .newsletter-box {
            grid-column: 1 / -1;
            max-width: 600px;
            margin: 0 auto;
          }
        }

        /* Mobile (768px and below) */
        @media (max-width: 768px) {
          .footer-main {
            padding: 48px 20px 32px;
          }

          .footer-grid {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .footer-logo {
            font-size: 2.25rem;
          }

          /* Accordion Styles for Mobile */
          .accordion-header {
            display: flex;
            padding: 20px 0;
            border-bottom: 1px solid rgba(196, 30, 58, 0.1);
          }

          .accordion-content {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease, padding 0.3s ease;
            padding: 0 0 0 4px;
          }

          .accordion-content.expanded {
            max-height: 500px;
            padding: 16px 0 24px 4px;
          }

          .footer-brand {
            margin-bottom: 8px;
          }

          .contact-list {
            flex-direction: column;
            align-items: center;
            gap: 12px;
          }

          .footer-contact-item:hover {
            transform: none;
          }

          .social-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .social-card {
            padding: 12px;
          }

          .footer-links {
            gap: 8px;
          }

          .footer-links a:hover {
            transform: none;
          }

          .newsletter-box {
            margin-top: 8px;
          }

          .newsletter-form {
            flex-direction: column;
          }

          .newsletter-btn {
            justify-content: center;
          }
        }

        /* Small Mobile (480px and below) */
        @media (max-width: 480px) {
          .footer-decor {
            width: 40px;
            height: 40px;
            top: -20px;
          }

          .footer-decor::before {
            font-size: 16px;
          }

          .footer-logo {
            font-size: 2rem;
          }

          .footer-tagline {
            font-size: 0.9rem;
          }

          .social-grid {
            grid-template-columns: 1fr;
          }

          .social-card {
            flex-direction: row;
            justify-content: flex-start;
          }

          .footer-bottom-content {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .legal-links {
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px;
          }

          .copyright {
            flex-direction: column;
            gap: 8px;
          }
        }

        /* Large Desktop (1440px and above) */
        @media (min-width: 1440px) {
          .footer-grid {
            gap: 64px;
          }

          .footer-logo {
            font-size: 3rem;
          }
        }

        /* Reduced Motion Preferences */
        @media (prefers-reduced-motion: reduce) {
          .footer-contact-item,
          .social-card,
          .newsletter-btn,
          .footer-links a,
          .accordion-content {
            transition: none;
          }
          
          .copyright svg {
            animation: none;
          }
        }

        /* High Contrast Mode Support */
        @media (prefers-contrast: high) {
          .site-footer {
            border-top-width: 4px;
          }
          
          .newsletter-box {
            border: 2px solid var(--text-dark);
          }
          
          .footer-contact-item a:hover,
          .footer-links a:hover {
            text-decoration: underline;
          }
        }
      `}</style>

      <div className="footer-bg-decoration" />
      <div className="footer-decor" />

      <div className="footer-main">
        <div className="footer-grid">
          {/* Brand Column - Always Visible */}
          <div className="footer-brand">
            <div className="footer-logo">Parivar Wedding Event</div>
            <p className="footer-tagline">
              Creating unforgettable moments for your special celebrations. 
              From intimate gatherings to grand destination weddings across India.
            </p>
            
            <div className="contact-list">
              <div className="footer-contact-item">
                <FaMapMarkerAlt size={18} />
                <span>Mumbai • Jaipur • Goa • Udaipur</span>
              </div>
              <div className="footer-contact-item">
                <FaEnvelope size={18} />
                <a href="mailto:hello@parivarwedding.in">hello@parivarwedding.in</a>
              </div>
              <div className="footer-contact-item">
                <FaPhone size={18} />
                <a href="tel:+918920514929">+91 89205 14929</a>
              </div>
              <div className="footer-contact-item">
                <FaWhatsapp size={18} />
                <a href="https://wa.me/918920514929" target="_blank" rel="noopener noreferrer">
                  WhatsApp Chat
                </a>
              </div>
            </div>
            
            <div className="owner-signature">
              <div className="owner-name">{OWNER_NAME}</div>
              <div className="owner-title">Founder & Event Director</div>
            </div>
          </div>

          {/* Quick Links - Accordion on Mobile */}
          <AccordionSection title="Explore" id="explore">
            <ul className="footer-links">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.path}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </AccordionSection>

          {/* Social Links - Accordion on Mobile */}
          <AccordionSection title="Follow Us" id="follow">
            <div className="social-grid">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-card"
                  aria-label={`${social.label} - ${social.followers}`}
                >
                  <div className="social-icon-wrap">
                    <social.icon size={20} />
                  </div>
                  <div className="social-info">
                    <span className="social-label">{social.label}</span>
                    <span className="social-followers">{social.followers}</span>
                  </div>
                </a>
              ))}
            </div>
          </AccordionSection>

          {/* Newsletter - Accordion on Mobile */}
          <AccordionSection title="Stay Updated" id="contact">
            <div className="newsletter-box">
              <p className="newsletter-text">
                Get wedding inspiration, venue guides, and exclusive offers delivered to your inbox.
              </p>
              <form className="newsletter-form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="newsletter-input"
                  required
                  aria-label="Email for newsletter"
                />
                <button type="submit" className="newsletter-btn">
                  <FaPaperPlane size={14} />
                  Join
                </button>
              </form>
              {subscribed && (
                <div className="newsletter-success" role="status">
                  <FaHeart size={14} /> Thanks for subscribing!
                </div>
              )}
            </div>
          </AccordionSection>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <div className="copyright">
            <FaHeart size={12} /> © {currentYear} Parivar Wedding Event. Made with love in India.
          </div>
          <div className="legal-links">
            <a href="#privacy">Privacy Policy</a>
            {openTerms ? (
              <button onClick={openTerms} type="button">
                Terms of Service
              </button>
            ) : (
              <a href="#terms">Terms of Service</a>
            )}
            <a href="#cookies">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;